/**
 * Utility functions for reordering arrays and managing order values
 *
 * @module reorder
 */

import logger from '../../utils/logger';

/**
 * Reorder an array by moving an item from one index to another
 *
 * Immutable operation (returns new array, doesn't mutate input).
 * Validates indices to prevent out-of-bounds access.
 *
 * @param items - Array to reorder
 * @param oldIndex - Current index of item to move (0-based)
 * @param newIndex - Target index for item (0-based)
 * @returns New array with item moved to new position, or original array if indices invalid
 *
 * @example
 * ```typescript
 * const items = ['A', 'B', 'C', 'D'];
 * const reordered = reorderArray(items, 0, 2);
 * // Result: ['B', 'C', 'A', 'D']
 *
 * // Invalid indices return original array unchanged
 * const unchanged = reorderArray(items, -1, 2);
 * // Result: ['A', 'B', 'C', 'D']
 * ```
 */
export function reorderArray<T>(
  items: T[],
  oldIndex: number,
  newIndex: number
): T[] {
  // Validate indices to prevent out-of-bounds access
  if (
    oldIndex < 0 ||
    oldIndex >= items.length ||
    newIndex < 0 ||
    newIndex >= items.length
  ) {
    logger.error('Reorder', 'Invalid indices:', {
      oldIndex,
      newIndex,
      length: items.length,
    });
    return items; // Return original array unchanged
  }

  // Create mutable copy for splicing (safe because original unchanged)
  const result = Array.from(items);
  const [removed] = result.splice(oldIndex, 1);
  result.splice(newIndex, 0, removed);
  return result;
}

/**
 * Assign gap-based order values to array of items
 *
 * Uses intervals of 10 to allow future insertions without renumbering.
 *
 * @param items - Array of items (order field will be overwritten)
 * @param startValue - Starting order value (default: 10)
 * @param increment - Gap between items (default: 10)
 * @returns New array with order field assigned
 *
 * @example
 * ```typescript
 * const items = [{ id: 'a' }, { id: 'b' }, { id: 'c' }];
 * const ordered = assignOrderValues(items);
 * // Result: [
 * //   { id: 'a', order: 10 },
 * //   { id: 'b', order: 20 },
 * //   { id: 'c', order: 30 }
 * // ]
 * ```
 */
export function assignOrderValues<T extends { order?: number | null }>(
  items: T[],
  startValue: number = 10,
  increment: number = 10
): Array<T & { order: number }> {
  return items.map((item, index) => ({
    ...item,
    order: startValue + index * increment,
  }));
}

/**
 * Calculate order value for item inserted between two existing items
 *
 * Uses midpoint to avoid renumbering.
 * If midpoint yields non-integer, rounds to nearest integer.
 *
 * @param prevOrder - Order value of item before insertion point (null = start)
 * @param nextOrder - Order value of item after insertion point (null = end)
 * @param defaultFirst - Default value if inserting at start (default: 10)
 * @param defaultLast - Default increment if inserting at end (default: 10)
 * @returns Calculated order value for new item
 *
 * @example
 * ```typescript
 * // Insert between items with order 10 and 20
 * const newOrder = calculateInsertionOrder(10, 20);
 * // Result: 15
 *
 * // Insert at start (before item with order 10)
 * const firstOrder = calculateInsertionOrder(null, 10);
 * // Result: 5 (defaultFirst / 2)
 *
 * // Insert at end (after item with order 100)
 * const lastOrder = calculateInsertionOrder(100, null);
 * // Result: 110 (prevOrder + defaultLast)
 * ```
 */
export function calculateInsertionOrder(
  prevOrder: number | null,
  nextOrder: number | null,
  defaultFirst: number = 10,
  defaultLast: number = 10
): number {
  if (prevOrder === null && nextOrder === null) {
    // Empty collection
    return defaultFirst;
  }

  if (prevOrder === null) {
    // Insert at start
    return Math.floor(nextOrder! / 2);
  }

  if (nextOrder === null) {
    // Insert at end
    return prevOrder + defaultLast;
  }

  // Insert between items
  const midpoint = (prevOrder + nextOrder) / 2;
  return Math.floor(midpoint);
}
